import { useFinanceCapabilities } from '@/hooks/use-finance-direct-reconcile'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, Badge } from 'antd'
import {
  HomeOutlined,
  AppstoreOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  ShoppingCartOutlined,
  ExperimentOutlined,
  CreditCardOutlined,
  FileOutlined,
  ReloadOutlined,
  CalendarOutlined,
  BellOutlined,
  SettingOutlined,
  DatabaseOutlined,
  InboxOutlined,
  UnorderedListOutlined,
  ToolOutlined,
  FileTextOutlined,
  TeamOutlined,
  BankOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import { useSidebarStore } from '@/stores/sidebar'
import { useViewRoleStore } from '@/stores/view-role'
import { useCurrentUser } from '@/hooks/use-auth'
import { useNotificationContext } from '@/contexts/notification-context'
import { useTaskBadges, type TaskBadges } from '@/hooks/use-task-badges'
import { menuConfig } from '@/lib/menu/config'
import { compactFinanceMenu, financeParentMenu } from '@/lib/menu/finance-presentation'
import { filterMenuByRole, filterMenuByRoles, isPathActive, menuContainsPath } from '@/lib/menu/utils'
import { MenuItem, UserRole } from '@/types/menu'
import type { MenuProps } from 'antd'

type MenuItemType = Required<MenuProps>['items'][number]

/**
 * Lucide 图标到 Ant Design Icons 的映射
 */
const iconMap: Record<string, React.ReactNode> = {
  LayoutDashboard: <HomeOutlined />,
  Package: <InboxOutlined />,
  Users: <TeamOutlined />,
  ClipboardList: <UnorderedListOutlined />,
  AlertCircle: <ExclamationCircleOutlined />,
  CreditCard: <CreditCardOutlined />,
  Receipt: <FileOutlined />,
  Calendar: <CalendarOutlined />,
  Award: <CheckOutlined />,
  Truck: <ShoppingCartOutlined />,
  Settings: <SettingOutlined />,
  Cpu: <DatabaseOutlined />,
  Wrench: <ToolOutlined />,
  Building: <BankOutlined />,
  Building2: <BankOutlined />,
  Beaker: <ExperimentOutlined />,
  Bell: <BellOutlined />,
  ShieldAlert: <SafetyOutlined />,
  Database: <AppstoreOutlined />,
  FileText: <FileTextOutlined />,
  RotateCcw: <ReloadOutlined />,
}

/**
 * 获取菜单项的 Ant Design Icon
 */
function getMenuIcon(item: MenuItem): React.ReactNode {
  if (!item.icon) return null
  // icon 是 LucideIcon 组件，获取其名称
  const iconName = item.icon.displayName || item.icon.name
  return iconMap[iconName] || <AppstoreOutlined />
}

/**
 * 顶层菜单分组（甲方诉求：把「任务」与「管理」在形式上分区，而非子菜单）。
 * - top：英雄入口（工作台），置顶不带分组标题
 * - task：任务流 / 审批流的「处理页」——日常要执行的工作流动作、角色任务工作台、我的处理记录
 * - management：其余一切非任务流/审批流页面——总览检索、配置、主数据、审计、通知中心 等
 * - other：客户端 my-* / 月结公司工作台等，保持扁平不分组
 * 仅当用户同时拥有任务项与管理项（员工）才渲染分组标题；客户/月结管理员保持扁平。
 */
const TOP_MENU_IDS = new Set(['dashboard'])

// 任务组：只放「任务流 / 审批流」的处理页
const TASK_MENU_IDS = new Set([
  'task-assign', 'task-dispatch', 'task-voucher-audit', // 任务流处理页
  'task-inspect-onsite', 'task-inspect-mail', 'task-outsource-testing', // 检测分叉:上门/邮寄
  'task-mail-receive', 'task-mail-dispatch', 'task-mail-return', // 邮寄分叉:收件/派单/寄回
  'task-invoice-approval', 'task-issue-invoice', 'task-cert', 'task-refund-approval', 'task-refund', // 含审批流节点
  'task-exception', 'task-monthly', 'task-payment-claim', 'task-payment-reconcile', 'task-reissue-approval',
  'task-voucher-assist', 'task-receipt-register', 'task-customer',
  'task-records', // 我的处理记录
])

// 管理组：非任务流/审批流的总览检索、配置、主数据、审计、通知中心 等
const MANAGEMENT_MENU_IDS = new Set([
  'orders', 'assisted-order-onsite', 'assisted-order-mail', 'assisted-order-outsource', 'orders-batch-import', 'exceptions', 'dispatch-overview', 'schedule-board', 'mail-workbench', 'outsource-workbench', 'inspection-records', 'return-ledger', 'certificates',
  'payments', 'invoices', 'refunds', 'monthly', 'market-workflow-records', 'market-payment-assistance', 'engineer-records',
  // 原「价目配置」目录拆平后的一级配置菜单
  'config-device-types', 'config-inspection-templates', 'config-additional-fees',
  'staff', 'customer', 'base-data',
  'notifications', 'audit-logs', 'impersonation-history',
])

function menuGroupOf(id: string): 'top' | 'task' | 'management' | 'other' {
  if (TOP_MENU_IDS.has(id)) return 'top'
  if (TASK_MENU_IDS.has(id)) return 'task'
  if (MANAGEMENT_MENU_IDS.has(id)) return 'management'
  return 'other'
}

/** 构建单个菜单项（含徽章 + 图标 + 子菜单），不含分组。 */
function buildNavItem(
  menu: MenuItem,
  unreadCount: number,
  taskBadges: TaskBadges
): MenuItemType {
  // 徽章：通知未读数(红) 或 待办计数(蓝)。一个菜单项至多一种,通知优先。
  let label: React.ReactNode = menu.title
  const notifCount = menu.badge === 'notification' ? unreadCount : 0
  const taskCount = menu.taskBadge ? taskBadges[menu.taskBadge] || 0 : 0
  const isNotif = notifCount > 0
  const badgeCount = notifCount || taskCount
  if (badgeCount > 0) {
    label = (
      <span className="flex min-w-0 items-center gap-2" title={menu.title}>
        <span className="min-w-0 truncate">{menu.title}</span>
        <Badge
          className="shrink-0"
          count={badgeCount > 99 ? '99+' : badgeCount}
          {...(isNotif ? { status: 'error' as const } : { color: '#1677ff' })}
        />
      </span>
    )
  }

  const item: MenuItemType = {
    key: menu.id,
    label,
    icon: getMenuIcon(menu),
  }

  if (menu.children && menu.children.length > 0) {
    return {
      ...item,
      children: menu.children.map((c) => buildNavItem(c, unreadCount, taskBadges)),
    }
  }
  return item
}

/**
 * 将菜单配置转换为 Ant Design Menu 数据结构，并按「业务 / 管理」分组。
 * 用 type:'group'（分组标题）而非子菜单实现"形式上分区"。
 */
function convertToNavItems(
  menus: MenuItem[],
  unreadCount: number,
  taskBadges: TaskBadges
): MenuItemType[] {
  const top: MenuItemType[] = []
  const task: MenuItemType[] = []
  const management: MenuItemType[] = []
  const other: MenuItemType[] = []

  for (const menu of menus) {
    const item = buildNavItem(menu, unreadCount, taskBadges)
    const g = menuGroupOf(menu.id)
    if (g === 'top') top.push(item)
    else if (g === 'task') task.push(item)
    else if (g === 'management') management.push(item)
    else other.push(item)
  }

  // 仅员工（同时有任务项与管理项）分「任务/管理」两组；客户/月结管理员保持扁平
  if (task.length === 0 || management.length === 0) {
    return [...top, ...task, ...management, ...other]
  }

  return [
    ...top,
    { type: 'group', label: '任务', children: task },
    { type: 'group', label: '管理', children: [...management, ...other] },
  ]
}

export interface SideNavProps {
  /** 自定义类名 */
  className?: string
}

/**
 * 侧边栏导航组件
 *
 * 基于 Semi Design Nav 组件构建，提供：
 * - 菜单分组和收缩
 * - 角色权限过滤
 * - 路由激活状态
 * - 通知徽章
 */
export function SideNav({ className = '' }: SideNavProps) {
  const direct = useFinanceCapabilities().data?.financeDirectReconcile === true
  const activeMenu = direct ? menuConfig.map(group => ({ ...group, children: group.children?.filter(item => !['task-receipt-register', 'task-payment-claim'].includes(item.id)) })).filter(item => !['task-receipt-register', 'task-payment-claim'].includes(item.id)) : menuConfig
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { data: currentUser } = useCurrentUser()
  const { unreadCount } = useNotificationContext()
  const { data: taskBadges } = useTaskBadges()

  // Zustand 状态
  const collapsed = useSidebarStore((state) => state.collapsed)
  const toggleCollapsed = useSidebarStore((state) => state.toggleCollapsed)
  const expandedMenus = useSidebarStore((state) => state.expandedMenus)
  const setMenuExpanded = useSidebarStore((state) => state.setMenuExpanded)

  // 菜单状态：初始为空数组（SSR 安全），客户端挂载后立即更新
  const [filteredMenus, setFilteredMenus] = useState<MenuItem[]>([])
  const [isHydrated, setIsHydrated] = useState(false)

  const actualRole = currentUser?.roles?.[0] as UserRole | undefined
  const allRoles = (currentUser?.roles as UserRole[] | undefined) || []
  const isMonthlyAdmin = allRoles.includes('MONTHLY_ADMIN')
  const { viewRole } = useViewRoleStore()

  // 客户端挂载后，立即从 localStorage 读取缓存的角色
  useEffect(() => {
    const cachedRole = localStorage.getItem('cached_user_role') as UserRole | null
    // 挂载瞬间 currentUser 可能未就绪：优先用其全部角色，回退到缓存的单角色
    const roles = (currentUser?.roles as UserRole[] | undefined) || (cachedRole ? [cachedRole] : [])
    if (roles.length > 0) {
      // 管理员主动切换视图角色 → 预览该单一角色；否则按用户全部角色并集（多角色同时可见）
      setFilteredMenus(
        roles.includes('ADMIN') && viewRole
          ? filterMenuByRole(activeMenu, viewRole)
          : filterMenuByRoles(activeMenu, roles)
      )
    }
    setIsHydrated(true)
  }, []) // 只在挂载时执行一次

  // 当用户角色或视角变化时，更新菜单
  useEffect(() => {
    if (!isHydrated) return // 等待客户端挂载完成

    if (allRoles.length > 0) {
      const ctx = { roles: allRoles, isMonthlyAdmin }
      // 管理员主动切换视图角色 → 预览该单一角色；否则按用户全部角色并集
      setFilteredMenus(
        allRoles.includes('ADMIN') && viewRole
          ? filterMenuByRole(activeMenu, viewRole, ctx)
          : filterMenuByRoles(activeMenu, allRoles, ctx)
      )
    }
  }, [actualRole, viewRole, isHydrated, isMonthlyAdmin, allRoles.join(','), direct])

  // 「工作台」总数角标要跟随「当前可见的任务节点」(已按角色/视角过滤)，而非后端按
  // 真实角色算的 workbenchTotal——否则管理员切到某角色视角时,总数仍含该角色无权处理的
  // 节点(如客服视角却把「异常处理」的待办计入)。前端按可见任务菜单项的角标求和覆盖之。
  const effectiveBadges = useMemo(() => {
    const badges = taskBadges || {}
    let total = 0
    const walk = (items: MenuItem[]) => {
      for (const it of items) {
        if (TASK_MENU_IDS.has(it.id) && it.taskBadge) total += badges[it.taskBadge] || 0
        if (it.children) walk(it.children)
      }
    }
    walk(filteredMenus)
    return { ...badges, workbenchTotal: total }
  }, [filteredMenus, taskBadges])

  const visibleMenus = compactFinanceMenu(filteredMenus, allRoles, viewRole)

  // 转换为 Ant Design Menu 数据结构
  const menuItems = useMemo(
    () => convertToNavItems(visibleMenus, unreadCount, effectiveBadges),
    [visibleMenus, unreadCount, effectiveBadges]
  )

  // 计算当前选中和展开的 key
  const selectedKeys = useMemo(() => {
    if (visibleMenus.length < filteredMenus.length) {
      const parent = financeParentMenu(pathname)
      if (parent && visibleMenus.some(menu => menu.id === parent)) return [parent]
    }
    // 收集所有匹配项，选 href「最具体（最长）」的那个高亮。
    // 避免「工作台」(/dashboard) 的前缀/工作台特殊匹配抢占更精确的子菜单
    // （如 派单总览 / 检测管理），也免去每加一个菜单就要手动维护白名单。
    const matches: { id: string; href: string }[] = []
    const walk = (menus: MenuItem[]) => {
      for (const menu of menus) {
        if (menu.href && isPathActive(menu.href, pathname)) {
          matches.push({ id: menu.id, href: menu.href })
        }
        if (menu.children) walk(menu.children)
      }
    }
    walk(visibleMenus)
    if (matches.length === 0) {
      const parent = financeParentMenu(pathname)
      return parent && visibleMenus.some(menu => menu.id === parent) ? [parent] : []
    }
    matches.sort(
      (a, b) => b.href.replace(/\/$/, '').length - a.href.replace(/\/$/, '').length
    )
    return [matches[0].id]
  }, [visibleMenus, filteredMenus, pathname])

  // 自动展开包含当前路径的父菜单
  useEffect(() => {
    filteredMenus.forEach((menu) => {
      if (menu.children && menuContainsPath(menu, pathname)) {
        if (!expandedMenus.has(menu.id)) {
          setMenuExpanded(menu.id, true)
        }
      }
    })
  }, [pathname, filteredMenus, expandedMenus, setMenuExpanded])

  // 展开的 key 数组
  const openKeys = useMemo(() => Array.from(expandedMenus), [expandedMenus])

  // 处理菜单选择
  const handleSelect = ({ key }: { key: string }) => {
    // 找到对应的菜单项
    const findMenu = (menus: MenuItem[], menuKey: string): MenuItem | null => {
      for (const menu of menus) {
        if (menu.id === menuKey) return menu
        if (menu.children) {
          const found = findMenu(menu.children, menuKey)
          if (found) return found
        }
      }
      return null
    }

    const menu = findMenu(filteredMenus, key)
    if (menu?.href) {
      navigate(menu.href, menu.id === 'invoices' ? { state: { financeMenuEntry: Date.now() } } : undefined)
    }
  }

  // 处理菜单展开/收起
  const handleOpenChange = (keys: string[]) => {
    // 找到新展开或收起的菜单项
    const newKey = keys.find((k) => !expandedMenus.has(k))
    const removedKey = Array.from(expandedMenus).find((k) => !keys.includes(k))

    if (newKey) {
      setMenuExpanded(newKey, true)
    } else if (removedKey) {
      setMenuExpanded(removedKey, false)
    }
  }

  return (
    <aside
      className={`fixed left-0 top-0 z-40 h-screen bg-white border-r border-slate-200 shadow-soft-sm transition-all duration-300 flex flex-col ${
        collapsed ? 'w-14' : 'w-48'
      } ${className}`}
    >
      {/* Logo 区域 - 紧凑高度 */}
      <div className="flex-shrink-0 flex items-center h-10 border-b border-slate-200 px-2">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center shadow-md shadow-blue-500/25">
            <SafetyOutlined className="text-white" style={{ fontSize: '14px' }} />
          </div>
          {!collapsed && (
            <span className="text-sm font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent whitespace-nowrap">
              中检服务
            </span>
          )}
        </Link>
      </div>

      {/* 导航菜单 - 占据剩余空间，内部可滚动 */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
        {isHydrated && filteredMenus.length > 0 ? (
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            items={menuItems}
            onSelect={handleSelect}
            onClick={({ key }) => { if (key === 'invoices' && selectedKeys.includes(key)) handleSelect({ key }) }}
            onOpenChange={handleOpenChange}
            style={{ borderRight: 0 }}
          />
        ) : (
          /* 骨架屏：水合前或菜单加载中 */
          <div className="p-2 space-y-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        )}
      </div>
    </aside>
  )
}
